import { decodeJwtPayload } from "./jwt";

/**
 * Tenant / store id from JWT or API. Returns null for missing or bogus values
 * so URLs are never built as `?restaurantId=null`.
 */
export function normalizeRestaurantId(raw) {
  if (raw == null) return null;
  const s =
    typeof raw === "string" ? raw.trim() : String(raw).trim();
  if (!s || s.toLowerCase() === "null" || s === "undefined") return null;
  return s;
}

/** Signed-in user id from JWT (`payload.user.id`). */
export function userIdFromAuthToken() {
  if (typeof localStorage === "undefined") return null;
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token);
    const id = payload?.user?.id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

/** Role ids assigned to the signed-in user (from cached access-summary). */
export function roleIdsAssignedToCurrentUser(summary) {
  const assignments = summary?.assignments;
  if (!Array.isArray(assignments) || !assignments.length) return new Set();
  const ids = new Set();
  for (const row of assignments) {
    const rid = row?.roleId?._id ?? row?.roleId;
    if (rid != null) ids.add(String(rid));
  }
  return ids;
}

/** `restaurantId` on the signed-in JWT (`payload.user`), if the backend put it there. */
export function restaurantIdFromAuthToken() {
  if (typeof localStorage === "undefined") return null;
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token);
    return normalizeRestaurantId(payload?.user?.restaurantId);
  } catch {
    return null;
  }
}

export const getImageUrl = (path) => {

    // If path is already a full URL, return it directly
    if (path && (path.startsWith('http://') || path.startsWith('https://'))) {
        return path;
    }
    
    const baseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:5001';
    return `${baseUrl}${path || 'uploads/default.jpg'}`;
};

/**
 * Human-readable platform label (e.g. DIGITAL_CATALOG → "Digital Catalog").
 * Uses i18n `platformName.*` when available; otherwise title-cases the enum.
 * @param {string} platform
 * @param {(key: string, defaultValue?: string) => string} [t]
 */
export function formatPlatformName(platform, t) {
  if (!platform) return '';
  const key = String(platform).trim().toUpperCase().replace(/[\s-]+/g, '_');
  const i18nKey = `platformName.${key}`;
  if (typeof t === 'function') {
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}